import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useParams, useNavigate, useLocation} from 'react-router-dom'
import {v4 as uuidv4} from 'uuid'
import {TrashIcon, DocumentCheckIcon, CheckCircleIcon, ArrowsRightLeftIcon, ArrowLeftIcon} from '@heroicons/react/24/outline'
import {Card} from '../../components/ui/Card'
import {Input} from '../../components/ui/Input'
import {Button} from '../../components/ui/Button'
import {Switch} from '../../components/ui/Switch'
import {useToast} from '../../hooks/useToast'
import {useIngredients} from '../../hooks/useIngredients'
import {ToastContainer} from '../../components/ui/Toast'
import {ConfirmDialog} from '../../components/ui/ConfirmDialog'
import {IngredientsService} from '../../services/api/ingredientsService'
import {RatingSelector, RatingButton} from '../../components/ingredients/RatingSelector'
import {MergeIngredientDialog} from '../../components/ingredients/MergeIngredientDialog'
import {
  Ingredient,
  IngredientDetails,
  Rating,
  RatingPair,
  RATING_CATEGORIES,
  RATING_TRANSLATIONS
} from '../../types/ingredient'
import {logger} from '../../utils/logger'

export const IngredientDetailsPage: React.FC = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const location = useLocation()
  const {toasts, showToast, removeToast} = useToast()
  const {deleteIngredient: deleteIngredientStore, invalidateCache} = useIngredients()

  const isNewIngredient = !id || id === 'new'
  const onSubmittedCallback = location.state?.onSubmitted as ((ingredient: Ingredient) => void) | undefined
  const initialData = location.state?.initialData as Partial<IngredientDetails> | undefined
  const fromProduct = location.state?.fromProduct as boolean | undefined
  const productId = location.state?.productId as string | undefined

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const fetchedRef = useRef(false)
  const [ingredientDetails, setIngredientDetails] = useState<IngredientDetails | null>(null)
  const [showMergeDialog, setShowMergeDialog] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // Form state
  const [inciName, setInciName] = useState(initialData?.inciName || '')
  const [commonName, setCommonName] = useState(initialData?.commonName || '')
  const [casNumber, setCasNumber] = useState('')
  const [ecNumber, setEcNumber] = useState('')
  const [comments, setComments] = useState('')

  const [hasEssentialOils, setHasEssentialOils] = useState(false)
  const [hasAllergens, setHasAllergens] = useState(false)
  const [hasEcoToxic, setHasEcoToxic] = useState(false)
  const [hasEndocrineDisruptors, setHasEndocrineDisruptors] = useState(false)

  const [selectedSkinTypes, setSelectedSkinTypes] = useState<RatingPair[]>([])
  const [selectedSkinConditions, setSelectedSkinConditions] = useState<RatingPair[]>([])
  const [selectedSpecificities, setSelectedSpecificities] = useState<RatingPair[]>([])
  const [selectedAllergies, setSelectedAllergies] = useState<RatingPair[]>([])
  const [selectedHairTypes, setSelectedHairTypes] = useState<RatingPair[]>([])
  const [selectedHairConditions, setSelectedHairConditions] = useState<RatingPair[]>([])
  const [selectedEyes, setSelectedEyes] = useState<RatingPair[]>([])
  const [selectedLipTypes, setSelectedLipTypes] = useState<RatingPair[]>([])
  const [selectedNails, setSelectedNails] = useState<RatingPair[]>([])

  const mergeRatingPairs = (allKeys: string[], apiPairs: RatingPair[]): RatingPair[] => {
    const apiPairsMap = new Map(apiPairs.map(pair => [pair.jsonKey, pair]))
    return allKeys.map(key => apiPairsMap.get(key) || {jsonKey: key, rating: null})
  }

  const loadIngredientData = useCallback((details: IngredientDetails) => {
    setInciName(details.inciName)
    setCommonName(details.commonName || '')
    setCasNumber(details.casNumber || '')
    setEcNumber(details.ecNumber || '')
    setComments(details.comments || '')

    setHasEssentialOils(details.hasEssentialOils)
    setHasAllergens(details.hasAllergens)
    setHasEcoToxic(details.hasEcoToxic)
    setHasEndocrineDisruptors(details.hasEndocrineDisruptors)

    // Load rating pairs with defaults
    setSelectedSkinTypes(mergeRatingPairs(RATING_CATEGORIES.skinTypes, details.skinTypes))
    setSelectedSkinConditions(mergeRatingPairs(RATING_CATEGORIES.skinConditions, details.skinConditions))
    setSelectedSpecificities(mergeRatingPairs(RATING_CATEGORIES.specificities, details.specificities))
    setSelectedAllergies(mergeRatingPairs(RATING_CATEGORIES.allergies, details.allergies))
    setSelectedHairTypes(mergeRatingPairs(RATING_CATEGORIES.hairTypes, details.hairTypes))
    setSelectedHairConditions(mergeRatingPairs(RATING_CATEGORIES.hairConditions, details.hairConditions))
    setSelectedEyes(mergeRatingPairs(RATING_CATEGORIES.eyes, details.eyes))
    setSelectedLipTypes(mergeRatingPairs(RATING_CATEGORIES.lipTypes, details.lipTypes))
    setSelectedNails(mergeRatingPairs(RATING_CATEGORIES.nails, details.nails))
  }, [])

  const fetchIngredientDetails = useCallback(async () => {
    if (!id) return

    setLoading(true)
    try {
      const details = await IngredientsService.getIngredientDetails(id)
      setIngredientDetails(details)
      loadIngredientData(details)
    } catch (error) {
      logger.error('Error fetching ingredient details:', error)
      showToast('Erreur lors du chargement de l\'ingrédient', 'error')
    } finally {
      setLoading(false)
    }
  }, [id, showToast, loadIngredientData])

  const initializeEmptyRatings = () => {
    setSelectedSkinTypes(RATING_CATEGORIES.skinTypes.map(key => ({jsonKey: key, rating: null})))
    setSelectedSkinConditions(RATING_CATEGORIES.skinConditions.map(key => ({jsonKey: key, rating: null})))
    setSelectedSpecificities(RATING_CATEGORIES.specificities.map(key => ({jsonKey: key, rating: null})))
    setSelectedAllergies(RATING_CATEGORIES.allergies.map(key => ({jsonKey: key, rating: null})))
    setSelectedHairTypes(RATING_CATEGORIES.hairTypes.map(key => ({jsonKey: key, rating: null})))
    setSelectedHairConditions(RATING_CATEGORIES.hairConditions.map(key => ({jsonKey: key, rating: null})))
    setSelectedEyes(RATING_CATEGORIES.eyes.map(key => ({jsonKey: key, rating: null})))
    setSelectedLipTypes(RATING_CATEGORIES.lipTypes.map(key => ({jsonKey: key, rating: null})))
    setSelectedNails(RATING_CATEGORIES.nails.map(key => ({jsonKey: key, rating: null})))
  }

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])


  useEffect(() => {
    // Avoid double fetch in StrictMode
    if (fetchedRef.current) return

    if (!isNewIngredient) {
      fetchedRef.current = true
      fetchIngredientDetails()
    } else {
      initializeEmptyRatings()
    }

    // Reset on unmount
    return () => {
      fetchedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNewIngredient])

  const updateRating = (
    setter: React.Dispatch<React.SetStateAction<RatingPair[]>>,
    key: string,
    rating: Rating | null
  ) => {
    setter(prev => prev.map(pair =>
      pair.jsonKey === key ? {...pair, rating} : pair
    ))
  }

  const applyRatingToAll = (rating: Rating | null) => {
    // Apply to all rating categories
    setSelectedSkinTypes(prev => prev.map(pair => ({...pair, rating})))
    setSelectedSkinConditions(prev => prev.map(pair => ({...pair, rating})))
    setSelectedSpecificities(prev => prev.map(pair => ({...pair, rating})))
    setSelectedAllergies(prev => prev.map(pair => ({...pair, rating})))
    setSelectedHairTypes(prev => prev.map(pair => ({...pair, rating})))
    setSelectedHairConditions(prev => prev.map(pair => ({...pair, rating})))
    setSelectedEyes(prev => prev.map(pair => ({...pair, rating})))
    setSelectedLipTypes(prev => prev.map(pair => ({...pair, rating})))
    setSelectedNails(prev => prev.map(pair => ({...pair, rating})))
  }

  const isFormValid = (): boolean => {
    const isInciValid = inciName.trim().length > 0
    const allRatingsValid = [
      selectedSkinTypes,
      selectedSkinConditions,
      selectedSpecificities,
      selectedAllergies,
      selectedHairTypes,
      selectedHairConditions,
      selectedEyes,
      selectedLipTypes,
      selectedNails,
    ].every(list => list.every(pair => pair.rating !== null))

    return isInciValid && allRatingsValid
  }

  const buildIngredientDetails = (): Partial<IngredientDetails> => {
    return {
      uuid: ingredientDetails?.uuid || uuidv4(),
      inciName,
      commonName: commonName || "",
      casNumber: casNumber || "",
      ecNumber: ecNumber || "",
      hasEssentialOils,
      hasAllergens,
      hasEcoToxic,
      hasEndocrineDisruptors,
      skinTypes: selectedSkinTypes,
      skinConditions: selectedSkinConditions,
      specificities: selectedSpecificities,
      allergies: selectedAllergies,
      hairTypes: selectedHairTypes,
      hairConditions: selectedHairConditions,
      eyes: selectedEyes,
      lipTypes: selectedLipTypes,
      nails: selectedNails,
      comments: comments || "",
      toValidate: false,
    }
  }

  const handleSave = async (isDraft: boolean = false) => {
    if (!isFormValid()) {
      showToast('Veuillez remplir tous les champs obligatoires', 'error')
      return
    }

    setSaving(true)
    try {
      const details = buildIngredientDetails()

      if (isDraft) {
        details.toValidate = true
      }

      let resultIngredient: Ingredient

      if (isNewIngredient) {
        resultIngredient = await IngredientsService.createIngredient(details)
        showToast(
          isDraft
            ? 'Brouillon enregistré avec succès. Il est disponible dans la catégorie "À valider"'
            : 'Ingrédient créé avec succès',
          'success'
        )
      } else {
        resultIngredient = await IngredientsService.updateIngredient(details as IngredientDetails)

        // If ingredient was pending validation and we're not making a draft, validate it
        if (ingredientDetails?.toValidate && !isDraft) {
          resultIngredient = await IngredientsService.validateIngredient(details.uuid!)
          showToast('Ingrédient validé avec succès', 'success')
        } else {
          showToast(
            isDraft
              ? 'Brouillon enregistré avec succès'
              : 'Ingrédient mis à jour avec succès',
            'success'
          )
        }
      }

      // If saving as draft, call validateIngredient with toValidate=true
      if (isDraft && resultIngredient.uuid) {
        resultIngredient = await IngredientsService.setIngredientValidationStatus(resultIngredient.uuid, true)
      }

      // Dispatch event to refresh the ingredients list
      window.dispatchEvent(new CustomEvent('ingredientUpdated', {
        detail: {
          ingredient: resultIngredient,
          action: isDraft ? 'draft' : (ingredientDetails?.toValidate && !isDraft ? 'validated' : 'update'),
          ingredientId: resultIngredient.uuid
        }
      }))

      // Call callback if provided (for modal usage from product page)
      if (onSubmittedCallback) {
        onSubmittedCallback(resultIngredient)
      } else if (!isDraft) {
        // Navigate immediately to list after successful save/validation
        if (fromProduct && productId) {
          // Dispatch event to refresh the product's ingredients
          window.dispatchEvent(new CustomEvent('ingredientUpdated'))
          navigate(`/products/${productId}`)
        } else {
          // Navigate to ingredients list with success message
          navigate('/ingredients', {
            state: {
              showToast: true,
              toastMessage: ingredientDetails?.toValidate && !isDraft
                ? 'Ingrédient validé avec succès'
                : isNewIngredient
                  ? 'Ingrédient créé avec succès'
                  : 'Ingrédient mis à jour avec succès',
              toastType: 'success',
              triggerRefresh: true
            }
          })
        }
      }

      // If it's a draft, just stay on the page but refresh the ingredient data
      if (isDraft && resultIngredient.uuid && !onSubmittedCallback) {
        setIngredientDetails(resultIngredient as IngredientDetails)
      }
    } catch (error: unknown) {
      logger.error('Error saving ingredient:', error)
      if ((error as Error)?.message?.includes('already exists')) {
        showToast('Un ingrédient avec le même nom INCI existe déjà', 'error')
      } else {
        showToast('Erreur lors de la sauvegarde de l\'ingrédient', 'error')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!ingredientDetails?.uuid) return

    setDeleting(true)
    try {
      // Use the store's deleteIngredient which will automatically refresh the list
      await deleteIngredientStore(ingredientDetails.uuid)

      // Dispatch event to refresh the ingredients list
      window.dispatchEvent(new CustomEvent('ingredientUpdated', {
        detail: {action: 'deleted', ingredientId: ingredientDetails.uuid}
      }))

      // Navigate with state to show toast on the list page
      navigate('/ingredients', {
        state: {
          showToast: true,
          toastMessage: 'Ingrédient supprimé avec succès',
          toastType: 'success',
          triggerRefresh: true
        }
      })
    } catch (error) {
      logger.error('Error deleting ingredient:', error)
      showToast('Erreur lors de la suppression de l\'ingrédient', 'error')
    } finally {
      setDeleting(false)
    }
  }

  const handleMerged = (newIngredientId: string) => {
    // Invalidate the ingredients cache to force refresh when returning to the list
    invalidateCache()

    // Dispatch event to refresh the ingredients list
    window.dispatchEvent(new CustomEvent('ingredientUpdated', {
      detail: {action: 'merged', ingredientId: newIngredientId}
    }))

    // If we came from a product page, go back to it after merge
    if (fromProduct && productId) {
      // Dispatch event to refresh the product's ingredients
      window.dispatchEvent(new CustomEvent('ingredientUpdated'))
      navigate(`/products/${productId}`)
    } else {
      // Navigate to the merged ingredient
      navigate(`/ingredients/${newIngredientId}`)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  const showValidateButton = ingredientDetails?.toValidate || initialData?.toValidate

  return (
    <div className="max-w-6xl mx-auto p-6">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Header - Full width */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => {
                if (fromProduct && productId) {
                  navigate(`/products/${productId}`)
                } else {
                  navigate('/ingredients')
                }
              }}
              className="flex items-center gap-2"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              {fromProduct && productId ? 'Retour au produit' : 'Retour'}
            </Button>
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
              {isNewIngredient
                ? 'Nouvel ingrédient'
                : `Détails de l'ingrédient ${ingredientDetails?.inciName || ''}`
              }
            </h1>
          </div>
        </div>

        {/* Buttons aligned to the right */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => handleSave(true)}
            disabled={saving || !isFormValid()}
            className="flex items-center gap-2"
          >
            <DocumentCheckIcon className="h-5 w-5" />
            {saving ? 'Enregistrement...' : 'Enregistrer le brouillon'}
          </Button>
          <Button
            onClick={() => handleSave(false)}
            disabled={saving || !isFormValid()}
            className={`flex items-center gap-2 ${showValidateButton ? 'bg-green-600 hover:bg-green-700' : ''}`}
          >
            <CheckCircleIcon className="h-5 w-5" />
            {saving
              ? 'Enregistrement...'
              : showValidateButton
                ? "Valider l'ingrédient"
                : 'Enregistrer'
            }
          </Button>
        </div>

        {/* Merge and Delete buttons below if not new */}
        {!isNewIngredient && (
          <div className="flex justify-end gap-3 mt-3">
            <Button
              variant="outline"
              onClick={() => setShowMergeDialog(true)}
              disabled={saving}
              className="flex items-center gap-2"
            >
              <ArrowsRightLeftIcon className="h-5 w-5" />
              Merger ingrédient
            </Button>
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={saving || deleting}
              className="border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
            >
              <TrashIcon className="h-5 w-5" />
              {deleting ? 'Suppression...' : 'Supprimer'}
            </Button>
          </div>
        )}
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Names Section */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Noms de l&apos;ingrédient</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="inci-name" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Nom INCI <span className="text-red-500">*</span>
              </label>
              <Input
                id="inci-name"
                type="text"
                value={inciName}
                onChange={(e) => setInciName(e.target.value)}
                className="w-full"
                required
              />
            </div>
            <div>
              <label htmlFor="common-name" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Nom commercial
              </label>
              <Input
                id="common-name"
                type="text"
                value={commonName}
                onChange={(e) => setCommonName(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </Card>

        {/* Basic Information */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Informations de base</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="cas-number" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Numéro CAS
              </label>
              <Input
                id="cas-number"
                type="text"
                value={casNumber}
                onChange={(e) => setCasNumber(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="ec-number" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Numéro EC
              </label>
              <Input
                id="ec-number"
                type="text"
                value={ecNumber}
                onChange={(e) => setEcNumber(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </Card>

        {/* Risks */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Risques</h2>
          <div className="space-y-3">
            <Switch
              checked={hasEssentialOils}
              onChange={setHasEssentialOils}
              label="Huile essentielle"
            />
            <Switch
              checked={hasAllergens}
              onChange={setHasAllergens}
              label="Allergène règlementé"
            />
            <Switch
              checked={hasEcoToxic}
              onChange={setHasEcoToxic}
              label="Potentiel Écotoxique"
            />
            <Switch
              checked={hasEndocrineDisruptors}
              onChange={setHasEndocrineDisruptors}
              label="Perturbateur endocrinien"
            />
          </div>
        </Card>

        {/* Global Rating Selector */}
        <div className="p-6 bg-gray-100 dark:bg-gray-900/50 rounded-lg shadow-md border border-gray-300 dark:border-gray-700">
          <div className="flex items-center justify-between px-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Application rapide des notes
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Cliquez sur une note pour l'appliquer à toutes les catégories ci-dessous
              </p>
            </div>
            <div className="flex gap-2">
              <RatingButton rating={Rating.A} isSelected={false} onClick={() => applyRatingToAll(Rating.A)} />
              <RatingButton rating={Rating.B} isSelected={false} onClick={() => applyRatingToAll(Rating.B)} />
              <RatingButton rating={Rating.C} isSelected={false} onClick={() => applyRatingToAll(Rating.C)} />
            </div>
          </div>
        </div>

        {/* Skin Types and Conditions */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Types de peau et états</h2>
          <div className="space-y-6">
            <RatingSelector
              title="Types de peau *"
              items={RATING_CATEGORIES.skinTypes}
              selectedRatings={selectedSkinTypes}
              onRatingChanged={(key, rating) => updateRating(setSelectedSkinTypes, key, rating)}
              getLabel={(key) => RATING_TRANSLATIONS.skinTypes[key] || key}
            />
            <RatingSelector
              title="États de peau *"
              items={RATING_CATEGORIES.skinConditions}
              selectedRatings={selectedSkinConditions}
              onRatingChanged={(key, rating) => updateRating(setSelectedSkinConditions, key, rating)}
              getLabel={(key) => RATING_TRANSLATIONS.skinConditions[key] || key}
            />
          </div>
        </Card>

        {/* Hair Types and Conditions */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Types de cheveux et états</h2>
          <div className="space-y-6">
            <RatingSelector
              title="Types de cheveux *"
              items={RATING_CATEGORIES.hairTypes}
              selectedRatings={selectedHairTypes}
              onRatingChanged={(key, rating) => updateRating(setSelectedHairTypes, key, rating)}
              getLabel={(key) => RATING_TRANSLATIONS.hairTypes[key] || key}
            />
            <RatingSelector
              title="États de cheveux *"
              items={RATING_CATEGORIES.hairConditions}
              selectedRatings={selectedHairConditions}
              onRatingChanged={(key, rating) => updateRating(setSelectedHairConditions, key, rating)}
              getLabel={(key) => RATING_TRANSLATIONS.hairConditions[key] || key}
            />
          </div>
        </Card>

        {/* Other Characteristics */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Autres caractéristiques</h2>
          <div className="space-y-6">
            <RatingSelector
              title="Yeux *"
              items={RATING_CATEGORIES.eyes}
              selectedRatings={selectedEyes}
              onRatingChanged={(key, rating) => updateRating(setSelectedEyes, key, rating)}
              getLabel={(key) => RATING_TRANSLATIONS.eyes[key] || key}
            />
            <RatingSelector
              title="Lèvres *"
              items={RATING_CATEGORIES.lipTypes}
              selectedRatings={selectedLipTypes}
              onRatingChanged={(key, rating) => updateRating(setSelectedLipTypes, key, rating)}
              getLabel={(key) => RATING_TRANSLATIONS.lipTypes[key] || key}
            />
            <RatingSelector
              title="Ongles *"
              items={RATING_CATEGORIES.nails}
              selectedRatings={selectedNails}
              onRatingChanged={(key, rating) => updateRating(setSelectedNails, key, rating)}
              getLabel={(key) => RATING_TRANSLATIONS.nails[key] || key}
            />
          </div>
        </Card>

        {/* Specificities and Allergies */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Spécificités et allergies</h2>
          <div className="space-y-6">
            <RatingSelector
              title="Spécificités *"
              items={RATING_CATEGORIES.specificities}
              selectedRatings={selectedSpecificities}
              onRatingChanged={(key, rating) => updateRating(setSelectedSpecificities, key, rating)}
              getLabel={(key) => RATING_TRANSLATIONS.specificities[key] || key}
            />
            <RatingSelector
              title="Allergies *"
              items={RATING_CATEGORIES.allergies}
              selectedRatings={selectedAllergies}
              onRatingChanged={(key, rating) => updateRating(setSelectedAllergies, key, rating)}
              getLabel={(key) => RATING_TRANSLATIONS.allergies[key] || key}
            />
          </div>
        </Card>

        {/* Comments */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Commentaire</h2>
          <label htmlFor="comments" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
            Commentaires
          </label>
          <textarea
            id="comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 dark:placeholder-gray-500"
            rows={3}
            placeholder="Commentaires..."
          />
        </Card>
      </div>

      {/* Merge Dialog */}
      {ingredientDetails && (
        <MergeIngredientDialog
          isOpen={showMergeDialog}
          onClose={() => setShowMergeDialog(false)}
          currentIngredient={ingredientDetails}
          onMerged={handleMerged}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer l'ingrédient "${ingredientDetails?.inciName || ''}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />
    </div>
  )
}